import { useEffect, useContext, useRef } from "react";
import TrackingContext from "./TrackingContext";
import { User } from "firebase/auth";

export type Args = {
  user: User;
  isAuthenticated: boolean;
  debug: { enabled: boolean };
};

/**
 * Hook to sync auth state with tracking services
 * @param {Object} props
 * @param {Object|null} props.user - The enriched user object containing both auth and profile data
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Object} [props.debug] - Debug options
 * @param {boolean} [props.debug.enabled] - Whether debug logging is enabled
 */
export function useAuthTracking({
  user,
  isAuthenticated,
  debug = { enabled: false },
}: Args) {
  const { setUserProperties, resetUser, isInitialized } =
    useContext(TrackingContext);
  const lastUserIdRef = useRef(null);

  useEffect(() => {
    // Function to handle setting user properties
    const handleUserTracking = async () => {
      try {
        // Early return if tracking isn't initialized yet
        if (!isInitialized) {
          if (debug.enabled) {
            console.log(
              "[useAuthTracking] Tracking not initialized yet, skipping user property sync"
            );
          }
          return;
        }

        // Handle user logout/reset
        if (!isAuthenticated || !user) {
          if (debug.enabled) {
            console.log(
              "[useAuthTracking] User logged out or not present, resetting tracking"
            );
          }

          // Only reset if we previously had a user
          if (lastUserIdRef.current) {
            await resetUser().catch((error) => {
              console.error(
                "[useAuthTracking] Error resetting user tracking:",
                error
              );
            });
            lastUserIdRef.current = null;
          }
          return;
        }

        // Skip if user ID hasn't changed
        if (lastUserIdRef.current === user.id) {
          if (debug.enabled) {
            console.log("[useAuthTracking] User ID unchanged, skipping update");
          }
          return;
        }

        if (debug.enabled) {
          console.log("[useAuthTracking] Setting user properties for user:", {
            user_id: user.id, // Internal ID
            firebase_uid: user.firebase_uid,
            has_organization: !!user.organization,
            has_role: !!user.role,
            // Log additional non-sensitive data
            org_name: user.organization?.name || "None",
          });
        }

        // Set user properties in tracking services
        const properties = {
          user_id: user.id, // Internal ID as primary identifier
          firebase_uid: user.firebase_uid, // Keep Firebase UID as additional property
          organization_id: user.organization?.id,
          organization_name: user.organization?.name,
          user_role: user.role,
        };

        if (debug.enabled) {
          console.log(
            "[useAuthTracking] Sending properties to tracking services:",
            properties
          );
        }

        await setUserProperties(properties).catch((error) => {
          console.error(
            "[useAuthTracking] Error setting user properties:",
            error
          );
        });

        // Update last known user ID (using internal ID)
        lastUserIdRef.current = user.id;

        if (debug.enabled) {
          console.log("[useAuthTracking] Successfully updated user properties");
        }
      } catch (error) {
        // Log error but don't throw - we don't want to break the app
        console.error(
          "[useAuthTracking] Unexpected error in auth tracking:",
          error
        );
      }
    };

    // Execute in the background
    handleUserTracking();
  }, [
    user,
    isAuthenticated,
    isInitialized,
    setUserProperties,
    resetUser,
    debug.enabled,
  ]);
}

export default useAuthTracking;
